import React, { useEffect, useState } from "react";

import { PaymentsSubscriptionUpdateAttributes } from "~/api/Api";
import { clamp, roundUpToMultiple } from "~/helpers/helpers";

import { TeamUsage } from "../../../api/types";
import { DEFAULT_CURRENCY } from "../../../configs/config";
import { MappedData } from "../../../pages/billing/SubscriptionPage";
import { getPrice } from "../../pricing/utils";
import Switch from "../../switch/Switch";
import Button from "../../ui/button/Button";
import RangeSlider from "../../ui/rangeSlider/RangeSlider";
import Spinner from "../../ui/spinner/Spinner";
import styles from "./styles.module.scss";

const MAX_QUANTITY_MULTIPLIER = 500;

export const calculatePriceFromSubscription = (
  currency: string,
  basePrice: number,
  addons: { quantity: number; price: { amount: number; divide_by: number } }[],
): string => {
  const addonPrices = addons.map(
    (addon) => (addon.quantity * addon.price.amount) / addon.price.divide_by,
  );

  const total = basePrice + addonPrices.reduce((acc, p) => acc + p, 0);

  return getPrice(total, currency);
};

type SubscriptionEdit = {
  name: string;
  price: number;
  interval: string;
  valid: string;
};

interface EditSubscriptionContentProps {
  editSubscriptionData: MappedData;
  onUpdatePlan: (data: PaymentsSubscriptionUpdateAttributes) => void;
  currentSubscription: SubscriptionEdit;
  isPendingUpdateSubscription: boolean;
  planUsage: TeamUsage;
}
const rangeTitles: { [key: string]: string } = {
  page_max: "pages",
  keyword_max: "keywords",
  site_max: "site",
};
const EditSubscriptionContent: React.FC<EditSubscriptionContentProps> = ({
  editSubscriptionData,
  onUpdatePlan,
  currentSubscription,
  isPendingUpdateSubscription,
}) => {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [switchValues, setSwitchValues] = useState<Record<string, number>>({});
  const [finalPrice, setFinalPrice] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialSwitchValues, setInitialSwitchValues] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    if (!isInitialized) {
      const initialQuantities: Record<string, number> = {};
      const initialSwitchState: Record<string, number> = {};

      editSubscriptionData.entries.forEach((entry) => {
        if (entry.singleton) {
          initialSwitchState[entry.key] = entry.quantity ?? 0;
        } else {
          const step = entry.divide_by ?? 1;
          const raw = entry.quantity ?? 0;
          initialQuantities[entry.key] = roundUpToMultiple(raw, step);
        }
      });

      setQuantities(initialQuantities);
      setSwitchValues(initialSwitchState);
      setInitialSwitchValues(initialSwitchState);
      setIsInitialized(true);
    }
  }, [editSubscriptionData, isInitialized]);

  const handleSwitchChange = (key: string, checked: boolean) => {
    setFinalPrice(null);
    setSwitchValues((prev) => {
      const newValues = {
        ...prev,
        [key]: checked ? 1 : 0,
      };

      return newValues;
    });
  };

  const computeDynamicPrice = (): string => {
    const addons = editSubscriptionData.entries.map((entry) => ({
      quantity: entry.singleton
        ? switchValues[entry.key] || 0
        : quantities[entry.key] || 0,
      price: {
        amount: entry.amount,
        divide_by: entry.divide_by || 1,
      },
    }));

    return calculatePriceFromSubscription(
      DEFAULT_CURRENCY,
      Number(currentSubscription.price),
      addons,
    );
  };

  const handleQuantityChange = (key: string, newQuantity: number) => {
    setFinalPrice(null);
    setQuantities((prev) => ({
      ...prev,
      [key]: newQuantity,
    }));
  };

  const handleUpdatePlan = () => {
    const priceToFreeze = computeDynamicPrice();
    setFinalPrice(priceToFreeze);

    const payload = {
      price_id: editSubscriptionData.priceId,
      subscription_addons_attributes: [
        ...Object.entries(quantities)
          .filter(([key]) => {
            const entry = editSubscriptionData.entries.find(
              (e) => e.key === key,
            );

            return entry && !entry.singleton;
          })
          .map(([key, quantity]) => ({
            quantity,
            // id: editSubscriptionData.entries.find(
            //   (entry) => entry.key === key,
            // )?.id,
            price_id: editSubscriptionData.entries.find(
              (entry) => entry.key === key,
            )?.price_id,
          })),
        ...Object.entries(switchValues)
          .filter(([key, value]) => {
            return value !== initialSwitchValues[key];
          })
          .map(([key, value]) => ({
            quantity: value,
            ...(value === 0 && { _destroy: true }),
            // id: editSubscriptionData.entries.find(
            //   (entry) => entry.key === key,
            // )?.id,
            price_id: editSubscriptionData.entries.find(
              (entry) => entry.key === key,
            )?.price_id,
          })),
      ],
    };
    // @ts-expect-error BE types
    onUpdatePlan(payload);
  };

  return (
    <div>
      <div className={styles.editplan}>
        <div>
          <h2 className={styles.editplanTitle}>{currentSubscription.name}</h2>
          <p className={styles.editplanPrice}>
            <span className={styles.editplanAmount}>
              {finalPrice !== null ? finalPrice : computeDynamicPrice()}
            </span>
            <span className={styles.editplanInterval}>
              {" "}
              / {currentSubscription.interval}
            </span>
          </p>
          <p>{currentSubscription.valid}</p>
        </div>
      </div>
      <div className={styles.editplanFeatures}>
        {editSubscriptionData.entries
          .filter((addon) => addon.singleton)
          .map((entry) => {
            return (
              <div key={entry.key}>
                <p>
                  {entry.title} for {getPrice(entry.amount, DEFAULT_CURRENCY)}
                </p>
                <Switch
                  checked={switchValues[entry.key] === 1}
                  onChange={(e) => {
                    if (typeof e === "boolean") {
                      handleSwitchChange(entry.key, e);
                    } else if (e && typeof e.target?.checked === "boolean") {
                      handleSwitchChange(entry.key, e.target.checked);
                    } else {
                      handleSwitchChange(
                        entry.key,
                        switchValues[entry.key] !== 1,
                      );
                    }
                  }}
                />
              </div>
            );
          })}

        {editSubscriptionData.entries
          .filter((addon) => !addon.singleton)
          .map((entry) => {
            const step = entry.divide_by ?? 1;
            const max = step * MAX_QUANTITY_MULTIPLIER;
            const raw = quantities[entry.key] ?? 0;
            const displayValue = clamp(roundUpToMultiple(raw, step), 0, max);

            return (
              <div key={entry.key}>
                <p>
                  {entry.title} {getPrice(entry.amount, DEFAULT_CURRENCY)} per{" "}
                  {entry.divide_by} {rangeTitles?.[entry.key]}
                </p>
                <RangeSlider
                  value={displayValue}
                  defaultValue={entry.quantity ?? 0}
                  min={0}
                  max={max}
                  step={step}
                  onChange={(newQuantity: number) => {
                    const rounded = clamp(
                      roundUpToMultiple(newQuantity, step),
                      0,
                      max,
                    );
                    handleQuantityChange(entry.key, rounded);
                  }}
                />
              </div>
            );
          })}
      </div>
      <Button
        fullWidth
        onClick={handleUpdatePlan}
        disabled={isPendingUpdateSubscription}
      >
        <span className={styles.editplanButton}>
          {isPendingUpdateSubscription && <Spinner size="1rem" />} Update Plan
        </span>
      </Button>
    </div>
  );
};

export default EditSubscriptionContent;
